import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Product } from "@/components/product-step/types";
import { BookingLanguage } from "@/types/booking";
import { languageMap } from "@/i18n";
import { useMembership } from "@/contexts/MembershipContext";
import {
  isBaselineBloodTest,
  isFollowupBloodTest,
} from "@/utils/bloodTestVisibility";
import { MembershipIncludedCard } from "./MembershipIncludedCard";
import { PackageCard } from "@/components/product-step/card/PackageCard";
import { ChevronDown } from "lucide-react";

interface MembershipBloodTestSectionProps {
  products: Product[];
  selectedProductId: string | null;
  onSelectPackage: (productId: string) => void;
  language: BookingLanguage;
  onSelectAndContinue?: (productId: string) => void;
  allProducts?: Product[];
}

/**
 * Membership-specific blood test layout:
 * - Featured "Included in your membership" card for the entitled product
 * - "View options" toggle → "Additional tests" with the alternative blood test
 */
export const MembershipBloodTestSection: React.FC<
  MembershipBloodTestSectionProps
> = ({
  products,
  selectedProductId,
  onSelectPackage,
  language,
  onSelectAndContinue,
  allProducts,
}) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  const {
    isMember,
    currentEntitlementType,
    showAllBloodTestsFlag,
    followUpCounter,
    isEntitledProduct,
    getEffectivePrice,
  } = useMembership();

  const [optionsExpanded, setOptionsExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Find the blood test package products
  const bloodTestPackages = products.filter(
    (p) =>
      p.product_type === "package" &&
      (isBaselineBloodTest(p.stripe_product_id) ||
        isFollowupBloodTest(p.stripe_product_id)),
  );

  const baselineProduct = bloodTestPackages.find((p) =>
    isBaselineBloodTest(p.stripe_product_id),
  );
  const followUpProduct = bloodTestPackages.find((p) =>
    isFollowupBloodTest(p.stripe_product_id),
  );

  // Determine which product is featured (preferred) and which is additional
  let featuredProduct: Product | undefined;
  let additionalProduct: Product | undefined;

  if (currentEntitlementType === "baseline_blood_test") {
    // Baseline entitled → featured, no additional shown
    featuredProduct = baselineProduct;
    additionalProduct = undefined;
  } else if (currentEntitlementType === "followup_blood_test") {
    // Followup entitled → featured, baseline as additional
    featuredProduct = followUpProduct;
    additionalProduct = baselineProduct;
  } else {
    // No entitlements left (or showAllBloodTests flag) → followup preferred, baseline additional
    featuredProduct = followUpProduct;
    additionalProduct = baselineProduct;
  }

  // Fallback: if follow-up product doesn't exist yet, show baseline as featured
  if (!featuredProduct) {
    featuredProduct = baselineProduct;
    additionalProduct = undefined;
  }

  if (!featuredProduct) return null;

  const isFeaturedEntitled = isEntitledProduct(
    featuredProduct.stripe_product_id,
  );
  const featuredMemberPrice = getEffectivePrice(featuredProduct);

  // Counter only shown for followup entitlements
  const showCounter =
    currentEntitlementType === "followup_blood_test" && followUpCounter;

  const handleSchedule = () => {
    onSelectPackage(featuredProduct!.id);
    if (onSelectAndContinue) {
      onSelectAndContinue(featuredProduct!.id);
    }
  };

  return (
    <div className="flex flex-col gap-6 w-full lg:items-center">
      {/* "Included in your membership" section — inner gap 8px */}
      <div className="flex flex-col gap-2 w-full lg:min-w-[500px] lg:max-w-[500px]">
        {isFeaturedEntitled && (
          <h3 className="text-base font-medium text-neutral-900 py-2">
            {t("membership.includedInYourMembership")}
          </h3>
        )}
        <MembershipIncludedCard
          product={featuredProduct}
          isEntitled={isFeaturedEntitled}
          memberPrice={featuredMemberPrice}
          counter={
            showCounter
              ? {
                  current: followUpCounter!.current,
                  total: followUpCounter!.total,
                }
              : null
          }
          remainingFollowUps={showCounter ? followUpCounter!.remaining : 0}
          language={language}
          onSchedule={handleSchedule}
        />
      </div>

      {/* "View options" toggle + Additional tests */}
      {additionalProduct && (
        <>
          {/* Toggle pill — h-48px p-4px per Figma */}
          <div className="flex items-center justify-center h-12 p-1">
            <button
              onClick={() => setOptionsExpanded(!optionsExpanded)}
              className="bg-white rounded-full pl-4 pr-2 py-2 flex items-center gap-2 shadow-sm hover:shadow-md transition-shadow normal-case"
            >
              <span className="text-sm font-medium text-neutral-900">
                {t("membership.viewOptions")}
              </span>
              <ChevronDown
                className={`w-5 h-5 text-neutral-500 transition-transform duration-200 ${
                  optionsExpanded ? "rotate-180" : ""
                }`}
              />
            </button>
          </div>

          {/* Additional tests section — animated grow/shrink */}
          <div
            ref={contentRef}
            className="w-full lg:min-w-[500px] lg:max-w-[500px] overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out"
            style={{
              maxHeight: optionsExpanded
                ? (contentRef.current?.scrollHeight ?? 2000) + "px"
                : "0px",
              opacity: optionsExpanded ? 1 : 0,
            }}
          >
            <div className="flex flex-col gap-2 w-full">
              <h3 className="text-base font-medium text-neutral-900 py-2">
                {t("membership.additionalTests")}
              </h3>
              <PackageCard
                product={additionalProduct}
                isSelected={selectedProductId === additionalProduct.id}
                onSelect={() => onSelectPackage(additionalProduct!.id)}
                language={language}
                onSelectAndContinue={
                  onSelectAndContinue
                    ? () => onSelectAndContinue(additionalProduct!.id)
                    : undefined
                }
                isOnlyProduct={true}
                allProducts={allProducts ?? products}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
